import React, {
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import TableContainer from "@material-ui/core/TableContainer";
import MuiTable from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import Paper from "@material-ui/core/Paper";
import SearchIcon from "@material-ui/icons/Search";

import { FiltersPayloads, SortPayload } from "@/types/ApiResponse";
import Loading from "@/components/StaticTable/Loading";

import { OperatorDataType } from "@/components/StaticTable/Filters/operators";
import useStyle from "./style";
import { Filters } from "components/StaticTable/Filters";
import Sort from "./Sort";
import {
  parseFiltersFromUrl,
  parseSortFromUrl,
  serializeFiltersToUrl,
  serializeSortToUrl,
} from "@/components/ApiTablePagine/helper";
import { useSearchParams } from "react-router-dom";

export interface DataStructure<T> {
  id?: string;
  renderer: (e: T) => ReactElement | string | number | null;
  label?: ReactNode;
  filter?: FilterInput | FilterInputList[];
  sortKey?: string;
  align?: "left" | "right" | "center";
  size?: "xs" | "s" | "m" | "l";
}

export interface FilterInput {
  key: string;
  rowType: OperatorDataType;
  nullable?: boolean;
}

interface FilterInputList {
  label: string;
  key: string;
  rowType: OperatorDataType;
}

interface StaticTableProps<T> {
  data: T[];
  dataStructure: DataStructure<T>[];
  identifyRow: (element: T) => T[keyof T];
  renderFooter?: () => ReactElement;
  filters?: FiltersPayloads;
  onSort?: (newSorting: SortPayload) => void;
  sort?: SortPayload;
  loading: boolean;
  setFilters?: Dispatch<SetStateAction<FiltersPayloads>>;
  elementsParPage?: number;
}

function StaticTable<T>({
  data,
  dataStructure,
  identifyRow,
  renderFooter,
  filters,
  onSort,
  loading,
  sort,
  setFilters,
  elementsParPage = 10,
}: StaticTableProps<T>) {
  const { t } = useTranslation();
  const classes = useStyle();
  const [searchParams, setSearchParams] = useSearchParams();
  const pushParamsToUrl = useRef(false);

  const [colonneSelectionneePourFiltre, setColonneSelectionneePourFiltre] =
    useState<DataStructure<T> | null>(null);

  useEffect(() => {
    const filterParam = searchParams.get("filters");
    const sortParam = searchParams.get("sort");

    const sortParamParse = parseSortFromUrl(sortParam);

    if (filterParam && setFilters) setFilters(parseFiltersFromUrl(filterParam));
    if (sortParam && onSort && sortParamParse) onSort(sortParamParse);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pushParamsToUrl.current) return;

    const serializedFilters = filters ? serializeFiltersToUrl(filters) : null;
    const serializedSort = sort ? serializeSortToUrl(sort) : null;

    if (serializedFilters) searchParams.set("filters", serializedFilters);
    else searchParams.delete("filters");

    if (serializedSort) searchParams.set("sort", serializedSort);
    else searchParams.delete("sort");

    setSearchParams(searchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sort]);

  return (
    <div style={{ paddingLeft: 40, paddingRight: 40 }}>
      <Filters
        dataStructure={dataStructure}
        colonneSelectionneePourFiltre={colonneSelectionneePourFiltre}
        setColonneSelectionneePourFiltre={setColonneSelectionneePourFiltre}
        filters={filters as FiltersPayloads}
        setFilters={(filters) => {
          pushParamsToUrl.current = true;
          if (setFilters) setFilters(filters);
        }}
      />

      <TableContainer component={Paper}>
        <MuiTable>
          <TableHead>
            <TableRow>
              {dataStructure?.map((column, index) => (
                <TableCell
                  key={index}
                  classes={{ head: classes.headerCell }}
                  style={
                    column.size
                      ? { width: sizeToWidth[column.size] }
                      : undefined
                  }
                >
                  <div
                    className={classes.headerCellWrapper}
                    style={{
                      display: "flex",
                      justifyContent: column.align || "center",
                    }}
                  >
                    <div>{column.label}</div>
                    {column.filter && setFilters && (
                      <div className={classes.headerCellButtonWrapper}>
                        <SearchIcon
                          onClick={() =>
                            setColonneSelectionneePourFiltre(column)
                          }
                        />
                      </div>
                    )}
                    {column.sortKey && onSort && (
                      <Sort
                        column={column}
                        sort={sort}
                        onSort={(sort) => {
                          pushParamsToUrl.current = true;
                          onSort(sort);
                        }}
                      />
                    )}
                  </div>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {loading ? (
            <Loading
              dataStructure={dataStructure}
              elementsParPage={elementsParPage}
            />
          ) : (
            <TableBody>
              {data && data.length !== 0 ? (
                data.map((item) => (
                  <TableRow
                    key={identifyRow(item) as string}
                    classes={{ root: classes.tableRow }}
                  >
                    {dataStructure?.map((column, index) => (
                      <TableCell
                        key={index}
                        classes={{ root: classes.cell }}
                        align={column?.align || "center"}
                      >
                        {column.renderer(item) || "-"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow classes={{ root: classes.tableRow }}>
                  <TableCell
                    style={{ padding: 30, fontSize: 15 }}
                    classes={{ root: classes.cell }}
                    align={"center"}
                    colSpan={dataStructure.length}
                  >
                    {t("common.dmri.paginate.noData")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}

          {!loading && renderFooter && data.length !== 0 && (
            <TableFooter>
              <TableRow>{renderFooter()}</TableRow>
            </TableFooter>
          )}
        </MuiTable>
      </TableContainer>
    </div>
  );
}

export const sizeToWidth: Record<string, string> = {
  xs: "10%",
  s: "20%",
  m: "30%",
  l: "50%",
};

export default StaticTable;
